import { css } from '@emotion/react';
import { IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import { useDraggable } from '@dnd-kit/core';
import { Link } from 'react-router';
import { colors } from '../../../../../theme/colors';
import { isValidConfigRoute, TEARSHEET_PANELS } from '../constants';
import { GridLayoutItem, TearsheetPanelType } from '../types';
import { ResizeHandle } from './ResizeHandle';

export interface DraggableCanvasItemData {
  itemId: string;
  panelType: TearsheetPanelType;
  currentX: number;
  currentY: number;
  w: number;
  h: number;
  source: 'canvas';
}

const panelSlotStyle = css`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: ${colors.neutral[5]};
  border: 1px solid ${colors.neutral[20]};
  border-radius: 4px;
  position: relative;
  transition: background 0.15s ease;
  box-sizing: border-box;
  cursor: grab;
  touch-action: none;

  &:hover {
    background: ${colors.neutral[10]};
  }

  &:active {
    cursor: grabbing;
  }
`;

const panelSlotDraggingStyle = css`
  opacity: 0.5;
  border-color: ${colors.primary[40]};
  cursor: grabbing;
`;

const panelActionsStyle = css`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.15s ease;
`;

const showControlsOnHoverStyle = css`
  &:hover .panel-controls {
    opacity: 1;
  }
`;

const actionButtonStyle = css`
  background: white;
  border: 1px solid ${colors.neutral[20]};
  padding: 0.25rem;

  &:hover {
    background: ${colors.neutral[10]};
  }
`;

const resizePreviewStyle = css`
  position: absolute;
  top: 0;
  left: 0;
  background: ${colors.primary[10]};
  border: 2px dashed ${colors.primary[40]};
  border-radius: 4px;
  pointer-events: none;
  z-index: 5;
`;

interface PanelSlotProps {
  item: GridLayoutItem;
  onRemove?: () => void;
  isResizing?: boolean;
  previewSize?: { w: number; h: number } | null;
  cellWidth?: number;
  cellHeight?: number;
  onResizeStart?: () => void;
  onResize?: (deltaX: number, deltaY: number) => void;
  onResizeEnd?: (deltaX: number, deltaY: number) => void;
}

export function PanelSlot({
  item,
  onRemove,
  isResizing,
  previewSize,
  cellWidth = 50,
  cellHeight = 50,
  onResizeStart,
  onResize,
  onResizeEnd,
}: PanelSlotProps) {
  const panelMeta = TEARSHEET_PANELS[item.panelType];

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `canvas-${item.i}`,
    data: {
      itemId: item.i,
      panelType: item.panelType,
      currentX: item.x,
      currentY: item.y,
      w: item.w,
      h: item.h,
      source: 'canvas',
    } as DraggableCanvasItemData,
  });

  return (
    <div
      ref={setNodeRef}
      css={[panelSlotStyle, showControlsOnHoverStyle, isDragging && panelSlotDraggingStyle]}
      {...listeners}
      {...attributes}
    >
      <Typography variant='body2' fontWeight={500}>
        {panelMeta.label}
      </Typography>
      <Typography variant='caption' color='text.secondary'>
        {panelMeta.description}
      </Typography>

      <div css={panelActionsStyle} className='panel-controls'>
        {isValidConfigRoute(panelMeta.configRoute) && (
          <IconButton
            css={actionButtonStyle}
            size='small'
            component={Link}
            to={`../${panelMeta.configRoute}`}
            title='Configure panel'
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <SettingsIcon fontSize='small' />
          </IconButton>
        )}
        {onRemove && (
          <IconButton
            css={actionButtonStyle}
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            onPointerDown={(e) => e.stopPropagation()}
            title='Remove panel'
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        )}
      </div>

      {onResizeStart && onResize && onResizeEnd && !isDragging && (
        <ResizeHandle onResizeStart={onResizeStart} onResize={onResize} onResizeEnd={onResizeEnd} />
      )}

      {isResizing && previewSize && (
        <div
          css={resizePreviewStyle}
          style={{
            width: previewSize.w * cellWidth,
            height: previewSize.h * cellHeight,
          }}
        />
      )}
    </div>
  );
}
